import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { amountToWordsSoles } from './amountToWords';
import { getVentasPaymentMethodLabel, VENTAS_PAYMENT_METHODS } from './ventasSalesConstants';
import { formatPrintDateOrDash } from './dateUtils';

const TEAL = [13, 148, 136];
const GRAY = [55, 65, 81];
const GRAY_MUTED = [107, 114, 128];
const GRAY_LIGHT = [156, 163, 175];
const BG_TABLE_ALT = [249, 250, 251];
const MARGIN = 14;

const fmtMoney = (n) =>
  parseFloat(n || 0).toLocaleString('es-PE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

function formatDate(value) {
  return formatPrintDateOrDash(value);
}

function formatDateTime(value) {
  if (!value) {
    return new Date().toLocaleString('es-PE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  }
  return new Date(value).toLocaleString('es-PE', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function drawSegments(doc, startX, y, segments) {
  let x = startX;
  segments.forEach((seg) => {
    if (seg.style === 'label') {
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...TEAL);
    } else if (seg.style === 'sep') {
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...GRAY_LIGHT);
    } else {
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...GRAY);
    }
    doc.setFontSize(9);
    doc.text(seg.text, x, y);
    x += doc.getTextWidth(seg.text) + (seg.style === 'sep' ? 2.5 : 1.2);
  });
  return x;
}

function drawSectionTitle(doc, y, title) {
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(...TEAL);
  doc.text(title.toUpperCase(), MARGIN, y);
  doc.setDrawColor(...TEAL);
  doc.setLineWidth(0.3);
  doc.line(MARGIN, y + 1.2, MARGIN + doc.getTextWidth(title.toUpperCase()) + 1, y + 1.2);
  return y + 5;
}

function moneyRow(label, amount) {
  return [label, `S/ ${fmtMoney(amount)}`];
}

/**
 * Genera y descarga el PDF oficial de cierre / liquidación de caja.
 * @param {object} report — payload del endpoint show (point_of_sale, sales_summary, liquidation, orders…)
 */
export async function downloadCashLiquidationPdf(report) {
  if (!report) {
    throw new Error('No hay datos de liquidación para generar el reporte');
  }

  const summary = report.sales_summary || {};
  const liquidation = report.liquidation || {};
  const pos = report.point_of_sale || {};
  const opening = Number(liquidation.opening_balance ?? 0);
  const cashSales = Number(summary.cash_sales ?? 0);
  const adjustment = Number(liquidation.cash_adjustment ?? 0);
  const closing =
    liquidation.closing_balance != null
      ? Number(liquidation.closing_balance)
      : Math.round((opening + cashSales + adjustment) * 100) / 100;
  const hasAdjustment = Math.abs(adjustment) >= 0.01;

  const posCode = pos.code || '';
  const posName = pos.name || 'POS';
  const shiftDate = report.shift_date;
  const safePos = String(posCode || posName).replace(/[^\w\-]+/g, '_');
  const safeDate = String(shiftDate || '').replace(/-/g, '');

  const doc = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  let y = MARGIN + 2;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.setTextColor(...TEAL);
  doc.text('CIERRE DE CAJA', pageWidth / 2, y, { align: 'center' });

  y += 2.8;
  doc.setDrawColor(...TEAL);
  doc.setLineWidth(0.5);
  doc.line(MARGIN, y, pageWidth - MARGIN, y);
  y += 5;

  drawSegments(doc, MARGIN, y, [
    { text: 'Punto de venta:', style: 'label' },
    { text: posCode ? `${posCode} — ${posName}` : posName, style: 'value' },
    { text: '·', style: 'sep' },
    { text: 'Fecha del turno:', style: 'label' },
    { text: formatDate(shiftDate), style: 'value' },
  ]);
  y += 4.2;

  drawSegments(doc, MARGIN, y, [
    { text: 'Estado del turno:', style: 'label' },
    { text: report.shift_status === 'closed' ? 'Cerrado' : 'Abierto', style: 'value' },
    { text: '·', style: 'sep' },
    { text: 'Emitido:', style: 'label' },
    { text: formatDateTime(liquidation.updated_at), style: 'value' },
  ]);
  y += 7;

  y = drawSectionTitle(doc, y, 'Resumen de ventas');

  autoTable(doc, {
    startY: y,
    head: [['Concepto', 'Monto']],
    body: [
      moneyRow(`Ventas del día (${summary.orders_count || 0} comprobante(s))`, summary.total_amount),
      moneyRow('Base imponible', summary.base_imponible),
      moneyRow('IGV', summary.igv_amount),
      moneyRow('Efectivo cobrado', cashSales),
      moneyRow('Pagos digitales (Yape / Plin / Transferencia)', summary.digital_total),
      moneyRow('Otros medios', summary.other_total),
    ],
    theme: 'grid',
    styles: {
      fontSize: 8.5,
      cellPadding: 2,
      textColor: GRAY,
      lineColor: [209, 213, 219],
      lineWidth: 0.2,
    },
    headStyles: {
      fillColor: TEAL,
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      fontSize: 8.5,
    },
    alternateRowStyles: { fillColor: BG_TABLE_ALT },
    columnStyles: {
      0: { cellWidth: pageWidth - MARGIN * 2 - 40 },
      1: { cellWidth: 40, halign: 'right', fontStyle: 'bold' },
    },
    margin: { left: MARGIN, right: MARGIN },
  });

  y = doc.lastAutoTable.finalY + 6;
  y = drawSectionTitle(doc, y, 'Desglose por medio de pago');

  const byMethodMap = {};
  (summary.by_method || []).forEach((row) => {
    byMethodMap[row.payment_method] = row;
  });

  autoTable(doc, {
    startY: y,
    head: [['Medio de pago', 'Ventas', 'Total']],
    body: VENTAS_PAYMENT_METHODS.map((opt) => {
      const row = byMethodMap[opt.value] || {};
      return [
        opt.label,
        String(row.orders_count || 0),
        `S/ ${fmtMoney(row.total_amount)}`,
      ];
    }),
    theme: 'grid',
    styles: {
      fontSize: 8.5,
      cellPadding: 2,
      textColor: GRAY,
      lineColor: [209, 213, 219],
      lineWidth: 0.2,
    },
    headStyles: {
      fillColor: TEAL,
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      fontSize: 8.5,
    },
    alternateRowStyles: { fillColor: BG_TABLE_ALT },
    columnStyles: {
      0: { cellWidth: pageWidth - MARGIN * 2 - 55 },
      1: { cellWidth: 22, halign: 'center' },
      2: { cellWidth: 33, halign: 'right', fontStyle: 'bold' },
    },
    margin: { left: MARGIN, right: MARGIN },
  });

  y = doc.lastAutoTable.finalY + 6;
  y = drawSectionTitle(doc, y, 'Arqueo de caja');

  const arqueoBody = [
    moneyRow(
      liquidation.opening_from_shift_date
        ? `Saldo inicial (desde cierre ${formatDate(liquidation.opening_from_shift_date)})`
        : 'Saldo inicial',
      opening
    ),
    moneyRow('(+) Efectivo cobrado', cashSales),
  ];
  if (hasAdjustment) {
    arqueoBody.push(
      moneyRow(
        adjustment >= 0 ? '(+) Ajuste de caja' : '(-) Ajuste de caja',
        Math.abs(adjustment)
      )
    );
  }
  arqueoBody.push(moneyRow('(=) Saldo final (sistema)', closing));

  autoTable(doc, {
    startY: y,
    head: [['Concepto', 'Monto']],
    body: arqueoBody,
    theme: 'grid',
    styles: {
      fontSize: 8.5,
      cellPadding: 2.2,
      textColor: GRAY,
      lineColor: [209, 213, 219],
      lineWidth: 0.2,
    },
    headStyles: {
      fillColor: TEAL,
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      fontSize: 8.5,
    },
    alternateRowStyles: { fillColor: BG_TABLE_ALT },
    columnStyles: {
      0: { cellWidth: pageWidth - MARGIN * 2 - 40 },
      1: { cellWidth: 40, halign: 'right', fontStyle: 'bold' },
    },
    margin: { left: MARGIN, right: MARGIN },
    didParseCell(data) {
      if (data.section === 'body' && data.row.index === arqueoBody.length - 1) {
        data.cell.styles.fontStyle = 'bold';
        data.cell.styles.fillColor = [204, 251, 241];
      }
    },
  });

  y = doc.lastAutoTable.finalY + 4;
  doc.setFont('helvetica', 'italic');
  doc.setFontSize(8);
  doc.setTextColor(...GRAY_MUTED);
  const words = amountToWordsSoles(closing);
  const wordLines = doc.splitTextToSize(`Saldo final en letras: ${words}`, pageWidth - MARGIN * 2);
  doc.text(wordLines, MARGIN, y);
  y += wordLines.length * 4 + 3;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(...GRAY);
  doc.text(
    'Este saldo final se usará como saldo inicial del día siguiente.',
    MARGIN,
    y
  );
  y += 6;

  const notes = (liquidation.notes || '').trim();
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8.5);
  doc.setTextColor(...TEAL);
  doc.text('Observaciones:', MARGIN, y);
  y += 4;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(...GRAY);
  const noteLines = doc.splitTextToSize(notes || 'Sin observaciones.', pageWidth - MARGIN * 2);
  doc.text(noteLines, MARGIN, y);
  y += noteLines.length * 3.8 + 6;

  const orders = Array.isArray(report.orders) ? report.orders : [];
  if (orders.length > 0) {
    if (y > pageHeight - 50) {
      doc.addPage();
      y = MARGIN + 4;
    }
    y = drawSectionTitle(doc, y, 'Detalle de ventas del día');

    autoTable(doc, {
      startY: y,
      head: [['Venta', 'Cliente', 'Medio', 'N° operación', 'Total']],
      body: orders.map((order) => [
        order.voucher || order.code || '—',
        order.client_name || '—',
        getVentasPaymentMethodLabel(order.payment_method),
        order.operation_number || '—',
        `S/ ${fmtMoney(order.total_amount)}`,
      ]),
      theme: 'grid',
      styles: {
        fontSize: 7.5,
        cellPadding: 1.6,
        textColor: GRAY,
        lineColor: [209, 213, 219],
        lineWidth: 0.2,
      },
      headStyles: {
        fillColor: TEAL,
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: 7.5,
      },
      alternateRowStyles: { fillColor: BG_TABLE_ALT },
      columnStyles: {
        0: { cellWidth: 32 },
        1: { cellWidth: 55 },
        2: { cellWidth: 28 },
        3: { cellWidth: 30 },
        4: { cellWidth: 27, halign: 'right', fontStyle: 'bold' },
      },
      margin: { left: MARGIN, right: MARGIN },
      didDrawPage(data) {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(7);
        doc.setTextColor(...GRAY_MUTED);
        doc.text(
          `Cierre de caja ${posCode || posName} · ${formatDate(shiftDate)} — Pág. ${data.pageNumber}`,
          pageWidth / 2,
          pageHeight - 8,
          { align: 'center' }
        );
      },
    });

    y = doc.lastAutoTable.finalY + 10;
  } else {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(...GRAY_MUTED);
    doc.text(
      `Cierre de caja ${posCode || posName} · ${formatDate(shiftDate)} — Pág. 1`,
      pageWidth / 2,
      pageHeight - 8,
      { align: 'center' }
    );
  }

  if (y > pageHeight - 42) {
    doc.addPage();
    y = MARGIN + 10;
  }

  const sigY = Math.max(y + 8, pageHeight - 40);
  const colW = (pageWidth - MARGIN * 2 - 20) / 2;
  const leftX = MARGIN + colW / 2;
  const rightX = MARGIN + colW + 20 + colW / 2;

  doc.setDrawColor(...GRAY_LIGHT);
  doc.setLineWidth(0.3);
  doc.line(MARGIN, sigY, MARGIN + colW, sigY);
  doc.line(MARGIN + colW + 20, sigY, pageWidth - MARGIN, sigY);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(...GRAY);
  doc.text('Responsable de caja', leftX, sigY + 5, { align: 'center' });
  doc.text('Nombre y firma', leftX, sigY + 9, { align: 'center' });
  doc.text('Supervisor / V°B°', rightX, sigY + 5, { align: 'center' });
  doc.text('Nombre y firma', rightX, sigY + 9, { align: 'center' });

  doc.save(`Cierre_Caja_${safePos}_${safeDate || 'sin_fecha'}.pdf`);
}

export default downloadCashLiquidationPdf;
